import { AnimatePresence, motion } from 'framer-motion'
import { Save } from 'lucide-react'
import { lazy, Suspense, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import FinishScreen from '../components/FinishScreen.jsx'
import ProgressBar from '../components/ProgressBar.jsx'
import AboutProject from '../components/StartPage/AboutProject.jsx'
import ConsentGate from '../components/StartPage/ConsentGate.jsx'
import Faq from '../components/StartPage/Faq.jsx'
import Hero from '../components/StartPage/Hero.jsx'
import Toast from '../components/Toast.jsx'
import ConfirmModal from '../components/common/ConfirmModal.jsx'
import PdfPreviewModal from '../components/common/PdfPreviewModal.jsx'
import RequiredFieldsBadge from '../components/common/RequiredFieldsBadge.jsx'
import AppFooter from '../components/layout/AppFooter.jsx'
import AppHeader from '../components/layout/AppHeader.jsx'

import '../css/App.css'
import '../css/index.css'

import { STEP, isFormStep } from '../constants/steps'
import { useDocumentLanguage } from '../hooks/useDocumentLanguage'
import { useEnterAdvancesFocus } from '../hooks/useEnterAdvancesFocus'
import { useFormData } from '../hooks/useFormData'
import { useFormProgress } from '../hooks/useFormProgress'
import { usePdfExport } from '../hooks/usePdfExport'
import { useUnloadWarning } from '../hooks/useUnloadWarning'
import useStepHistory from '../hooks/useStepHistory'
import { useWizardNavigation } from '../hooks/useWizardNavigation'
import { getRequiredDocs } from '../utils/getRequiredDocs'
import { MAX_IMPORT_BYTES } from '../utils/pdf/importLimits'
import { findDataSection } from './dataSections'

const Datenschutz = lazy(() => import('../components/Datenschutz.jsx'))
const Haftungsausschluss = lazy(() => import('../components/Haftungsausschluss.jsx'))
const Impressum = lazy(() => import('../components/Impressum.jsx'))
const SectionK = lazy(() => import('../components/SectionK.jsx'))
const SectionL = lazy(() => import('../components/SectionL.jsx'))
const SectionReview = lazy(() => import('../components/SectionReview.jsx'))

function App() {
	const { t, i18n } = useTranslation(['common', 'finish'])
	const { step, setStep, formData, updateData, mergeSectionData, resetFormData } = useFormData()
	const { globalProgress, milestones } = useFormProgress(formData)
	const requiredDocs = getRequiredDocs(formData)
	const navigation = useWizardNavigation(step, setStep)
	const { goToStep, goBack, isReviewEdit } = navigation

	// Files and the signature live in memory only; see useFormData for why.
	const [uploadedFiles, setUploadedFiles] = useState({})
	const [customDocs, setCustomDocs] = useState([])
	const [signatureData, setSignatureData] = useState(null)

	const [introStage, setIntroStage] = useState('hero')
	const [isDownloaded, setIsDownloaded] = useState(false)
	const [confirmModal, setConfirmModal] = useState(null)
	const [showImpressum, setShowImpressum] = useState(false)
	const [showDatenschutz, setShowDatenschutz] = useState(false)
	const [showHaftungsausschluss, setShowHaftungsausschluss] = useState(false)

	const showError = message => setConfirmModal({ message })

	// Everything personal is wiped as soon as the PDF has been handed to the
	// browser; the finish screen stays up with a confirmation.
	const wipePersonalData = () => {
		resetFormData({ keepStep: true })
		navigation.clearReturnMemory()
		setUploadedFiles({})
		setCustomDocs([])
		setSignatureData(null)
	}

	const pdfExport = usePdfExport({
		formData,
		files: uploadedFiles,
		signatureData,
		customDocs,
		onError: showError,
		onDownloaded: ({ keepData }) => {
			if (keepData) return
			wipePersonalData()
			setIsDownloaded(true)
		},
		fallbackError: t('finish:pdfErrorFallback'),
	})

	useDocumentLanguage(i18n.language)
	useEnterAdvancesFocus()
	useUnloadWarning(isFormStep(step))
	useStepHistory({
		step,
		locked: isDownloaded,
		onHistoryStep: target => {
			navigation.clearReturnMemory()
			if (target === STEP.START) setIntroStage('hero')
			setStep(target)
		},
	})

	useEffect(() => {
		window.scrollTo(0, 0)
	}, [step, introStage])

	const goHome = () => {
		setStep(STEP.START)
		setIntroStage('hero')
		window.scrollTo({ top: 0, behavior: 'smooth' })
	}

	const handleFinish = sigData => {
		setSignatureData(sigData)
		setStep(STEP.FINISH)
	}

	const importErrorMessage = error => {
		if (error.code === 'too-large') {
			return t('startPage:importErrorTooLarge', {
				size: Math.round(MAX_IMPORT_BYTES / (1024 * 1024)),
			})
		}
		if (error.code === 'not-pdf') return t('startPage:importErrorNotPdf')
		return t('startPage:importError')
	}

	const handleImportFile = async file => {
		try {
			const { readFormDataFromFile } = await import('../utils/pdf/pdfImporter')
			const extracted = await readFormDataFromFile(file)
			Object.keys(extracted).forEach(section => mergeSectionData(section, extracted[section]))
			setIntroStage('consent')
		} catch (error) {
			console.error('PDF import failed', error)
			showError(importErrorMessage(error))
		}
	}

	const startNewApplication = () => {
		wipePersonalData()
		setIsDownloaded(false)
		setIntroStage('hero')
		setStep(STEP.START)
		setConfirmModal(null)
	}

	// Nothing is left to lose after the download, so only ask before wiping a
	// form that is still filled in.
	const handleRestart = () => {
		if (isDownloaded) {
			startNewApplication()
			return
		}
		setConfirmModal({ message: t('finish:restartConfirm'), onConfirm: startNewApplication })
	}

	const activeSection = findDataSection(step)

	// Props every form section shares: its slice of the data and its updater.
	const sectionProps = key => ({
		data: formData[key],
		onChange: (field, value) => updateData(key, field, value),
	})

	return (
		<div className='app intro-scene'>
			<Toast message={t('common:toast.fileDownloaded')} visible={pdfExport.showToast} />

			<AppHeader onHome={goHome} />

			<main className='content'>
				<div className='layout-wrapper'>
					{isFormStep(step) && (
						<motion.div
							initial={{ opacity: 0, y: -20 }}
							animate={{ opacity: 1, y: 0 }}
							exit={{ opacity: 0, y: -20 }}
							transition={{ duration: 0.4 }}
						>
							<ProgressBar
								progress={globalProgress}
								steps={milestones}
								currentStep={step}
								onStepClick={navigation.jumpFromProgressBar}
							/>
							<div className='save-hint'>
								<Save size={20} />
								{t('common:saveHint')}
							</div>
							<RequiredFieldsBadge step={step} />
						</motion.div>
					)}

					<AnimatePresence mode='wait'>
						<motion.div
							key={step === STEP.START ? `${STEP.START}-${introStage}` : step}
							initial={{ opacity: 0, y: 10 }}
							animate={{ opacity: 1, y: 0 }}
							exit={{ opacity: 0, y: -10 }}
							transition={{ duration: 0.3 }}
							style={{ width: '100%' }}
						>
							<Suspense
								fallback={
									<p className='pulse-text' style={{ textAlign: 'center', padding: '60px 0' }}>
										{t('common:loader.loading')}
									</p>
								}
							>
								{step === STEP.START && introStage === 'hero' && (
									<>
										<Hero
											onStart={() => setIntroStage('consent')}
											onImportFile={handleImportFile}
										/>
										<AboutProject />
										<Faq />
									</>
								)}

								{step === STEP.START && introStage === 'consent' && (
									<ConsentGate
										onNext={() => setStep(STEP.A)}
										onBack={() => setIntroStage('hero')}
										onOpenDatenschutz={() => setShowDatenschutz(true)}
									/>
								)}

								{activeSection && (
									<activeSection.Component
										{...sectionProps(activeSection.key)}
										onBack={() => goBack(activeSection.prev)}
										onNext={() => goToStep(activeSection.next)}
										{...(activeSection.reviewEdit && { isReviewEdit })}
										{...(activeSection.usesSetStep && { setStep: goToStep })}
									/>
								)}

								{step === STEP.L && (
									<SectionL
										formData={formData}
										files={uploadedFiles}
										setFiles={setUploadedFiles}
										customDocs={customDocs}
										setCustomDocs={setCustomDocs}
										onBack={() =>
											goBack(
												formData.sectionE.receivesSocialAssistanceSGBXII === 'yes' ? STEP.E : STEP.J
											)
										}
										onNext={() => goToStep(STEP.REVIEW)}
									/>
								)}

								{step === STEP.REVIEW && (
									<SectionReview
										formData={formData}
										updateData={updateData}
										files={uploadedFiles}
										customDocs={customDocs}
										onBack={() => setStep(STEP.L)}
										onNext={() => setStep(STEP.K)}
										onEditStep={navigation.startEditFromReview}
									/>
								)}

								{step === STEP.K && (
									<SectionK
										{...sectionProps('sectionK')}
										onBack={() => setStep(STEP.REVIEW)}
										onFinish={handleFinish}
									/>
								)}

								{step === STEP.FINISH && (
									<FinishScreen
										requiredDocs={requiredDocs}
										uploadedFiles={uploadedFiles}
										isDownloaded={isDownloaded}
										isDownloading={pdfExport.isDownloading}
										onDownload={pdfExport.generate}
										onRestart={handleRestart}
										onBackToDocs={() => setStep(STEP.L)}
									/>
								)}
							</Suspense>
						</motion.div>
					</AnimatePresence>
				</div>
			</main>

			<AppFooter
				onOpenDatenschutz={() => setShowDatenschutz(true)}
				onOpenImpressum={() => setShowImpressum(true)}
				onOpenHaftungsausschluss={() => setShowHaftungsausschluss(true)}
			/>

			<Suspense fallback={null}>
				{showImpressum && <Impressum onClose={() => setShowImpressum(false)} />}
				{showDatenschutz && <Datenschutz onClose={() => setShowDatenschutz(false)} />}
				{showHaftungsausschluss && (
					<Haftungsausschluss onClose={() => setShowHaftungsausschluss(false)} />
				)}
			</Suspense>

			{confirmModal && (
				<ConfirmModal
					message={confirmModal.message}
					onConfirm={confirmModal.onConfirm ?? (() => setConfirmModal(null))}
					onCancel={confirmModal.onConfirm ? () => setConfirmModal(null) : undefined}
				/>
			)}

			{pdfExport.previewPdfBytes && (
				<PdfPreviewModal
					pdfBytes={pdfExport.previewPdfBytes}
					onDownload={pdfExport.download}
					onClose={pdfExport.closePreview}
				/>
			)}
		</div>
	)
}

export default App
